import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

interface VideoAnalysisData {
  id: string
  video_path: string
  status: 'processing' | 'completed' | 'failed'
  started_at: string
  processed_at?: string
  processing_time?: number
  analysis?: {
    summary?: string
    topics?: string[]
    sentiment?: string
    objects_detected?: string[]
    scenes?: Array<{
      timestamp: number
      description: string
    }>
    raw_response?: string
  }
  error_message?: string
}

interface VideoAnalysisProps {
  videoPath: string
  className?: string
}

export default function VideoAnalysis({ videoPath, className = '' }: VideoAnalysisProps) {
  const [analysisData, setAnalysisData] = useState<VideoAnalysisData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAnalysis()
    
    // Set up real-time subscription for updates
    const subscription = supabase
      .channel('video_processing_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'video_processing',
          filter: `video_path=eq.${videoPath}`
        },
        (payload) => {
          console.log('Processing update:', payload)
          if (payload.new) {
            setAnalysisData(payload.new as VideoAnalysisData)
          }
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [videoPath])

  const fetchAnalysis = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('video_processing')
        .select('*')
        .eq('video_path', videoPath)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      
      setAnalysisData(data)
    } catch (err) {
      console.error('Error fetching analysis:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
    } finally {
      setLoading(false)
    }
  }

  const formatProcessingTime = (ms?: number) => {
    if (!ms) return 'N/A'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return 'text-green-600 dark:text-green-400'
      case 'negative': return 'text-red-600 dark:text-red-400'
      default: return 'text-gray-600 dark:text-gray-400'
    }
  }

  const getSentimentIcon = (sentiment?: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': 
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clipRule="evenodd" />
          </svg>
        )
      case 'negative':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7.536 5.879a1 1 0 001.415 1.414 3 3 0 004.242 0 1 1 0 001.415-1.414 5 5 0 00-7.072 0z" clipRule="evenodd" />
          </svg>
        )
      default:
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-5 6a1 1 0 011-1h2a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        )
    }
  }

  if (loading) {
    return (
      <div className={`card ${className}`}>
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">
            Verificando análise do vídeo...
          </span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`card border-red-200 dark:border-red-700 ${className}`}>
        <div className="flex items-center p-4">
          <svg className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-red-700 dark:text-red-300">
            Erro ao carregar análise: {error}
          </span>
        </div>
      </div>
    )
  }

  if (!analysisData) {
    return (
      <div className={`card border-yellow-200 dark:border-yellow-700 ${className}`}>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-yellow-700 dark:text-yellow-300">
              Vídeo ainda não foi processado
            </span>
          </div>
          <button
            onClick={fetchAnalysis}
            className="btn-ghost px-3 py-1 text-sm"
          >
            Verificar novamente
          </button>
        </div>
      </div>
    )
  }

  if (analysisData.status === 'processing') {
    return (
      <div className={`card border-blue-200 dark:border-blue-700 ${className}`}>
        <div className="flex items-center p-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400 mr-3"></div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">
              Processando vídeo...
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Iniciado em {new Date(analysisData.started_at).toLocaleString('pt-BR')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (analysisData.status === 'failed') {
    return (
      <div className={`card border-red-200 dark:border-red-700 ${className}`}>
        <div className="p-4">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-red-700 dark:text-red-300 font-medium">
              Falha no processamento
            </span>
          </div>
          {analysisData.error_message && (
            <p className="text-sm text-red-600 dark:text-red-400 ml-8">
              {analysisData.error_message}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Status completed - show analysis results
  const analysis = analysisData.analysis

  return (
    <div className={`card ${className}`}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Análise de Vídeo
          </h3>
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            {formatProcessingTime(analysisData.processing_time)}
          </div>
        </div>

        {/* Summary */}
        {analysis?.summary && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              📝 Resumo
            </h4>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {analysis.summary}
            </p>
          </div>
        )}

        {/* Sentiment */}
        {analysis?.sentiment && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              😊 Sentimento
            </h4>
            <div className={`flex items-center ${getSentimentColor(analysis.sentiment)}`}>
              {getSentimentIcon(analysis.sentiment)}
              <span className="ml-2 capitalize">{analysis.sentiment}</span>
            </div>
          </div>
        )}

        {/* Topics */}
        {analysis?.topics && analysis.topics.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              🏷️ Tópicos
            </h4>
            <div className="flex flex-wrap gap-2">
              {analysis.topics.map((topic, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Objects Detected */}
        {analysis?.objects_detected && analysis.objects_detected.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              👁️ Objetos Detectados
            </h4>
            <div className="flex flex-wrap gap-2">
              {analysis.objects_detected.map((object, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm"
                >
                  {object}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Scenes */}
        {analysis?.scenes && analysis.scenes.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              🎬 Cenas Importantes
            </h4>
            <div className="space-y-2">
              {analysis.scenes.map((scene, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <span className="text-sm font-mono text-blue-600 dark:text-blue-400 mt-0.5">
                    {Math.floor(scene.timestamp / 60)}:{(scene.timestamp % 60).toString().padStart(2, '0')}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                    {scene.description}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>
              Processado em {analysisData.processed_at && new Date(analysisData.processed_at).toLocaleString('pt-BR')}
            </span>
            <button
              onClick={fetchAnalysis}
              className="btn-ghost px-2 py-1 text-xs"
            >
              Atualizar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
